import { useState, useEffect, useCallback, useMemo } from "react";
import { Link } from "react-router-dom";
import { all_routes } from "../../router/all_routes";
import ErrorModal from '../../../core/common/ErrorModal';
import SuccessModal from '../../../core/common/successModal';
import { fetchExceptionReportApi } from "../../../core/data/services/reportService";
import TooltipOption from "../../../core/common/tooltipOption";
import { exportToExcel, exportToPDF } from '../../../utils/exportUtils';
import PredefinedDateRanges from '../../../core/common/datePicker';
import { getUserDetails } from "../../../lib/getUserDetails";

interface MealExceptionData {
  id: number;
  classSectionId: string;
  schoolId: string;
  mealDate: string;
  mealServedCount: number;
  mealType: number;
  mealServed: boolean;
  mealServedRemarks: string;
  mealNotServedReasonId: number | null;
  notServedReason: string;
  className?: string;
  sectionName?: string;
  total?: number;
  teacherName?: string;
  actionBy?: string;
}

interface ExceptionSummary {
  totalExceptions: number;
  affectedClasses: number;
  affectedStudents: number;
  topReasons: Array<{
    reason: string;
    count: number;
    percentage: number;
  }>;
  dateWiseExceptions: Array<{
    date: string;
    count: number;
    affectedStudents: number;
    reasons: string[];
    mealTypes: string[];
  }>;
  weeklyTrend: Array<{
    week: string;
    count: number;
  }>;
  exceptionRate: number;
}

// Removed mock data

const MealExceptionReport = () => {
  const routes = all_routes;
  const [isLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [showErrorModal, setShowErrorModal] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);
  const [error, setError] = useState('');
  const [mealExceptions, setMealExceptions] = useState<MealExceptionData[]>([]);
  const [exceptionSummary, setExceptionSummary] = useState<ExceptionSummary | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [hoveredChartData, setHoveredChartData] = useState<any>(null);



  // Calculate exception summary statistics
  const calculateExceptionSummary = useCallback((exceptions: MealExceptionData[]) => {
    if (exceptions.length === 0) {
      setExceptionSummary({
        totalExceptions: 0,
        affectedClasses: 0,
        affectedStudents: 0,
        topReasons: [],
        dateWiseExceptions: [],
        weeklyTrend: [],
        exceptionRate: 0,
      });
      return;
    }

    const totalExceptions = exceptions.length;
    const affectedClasses = new Set(exceptions.map(e => e.classSectionId)).size;
    const affectedStudents = exceptions.reduce((sum, e) => sum + (e.mealServedCount || 0), 0);

    // Calculate top reasons
    const reasonCounts: { [key: string]: number } = {};
    exceptions.forEach(exception => {
      const reason = exception.notServedReason || exception.mealServedRemarks || 'No Reason Specified';
      reasonCounts[reason] = (reasonCounts[reason] || 0) + 1;
    });

    const topReasons = Object.entries(reasonCounts)
      .map(([reason, count]) => ({
        reason,
        count,
        percentage: totalExceptions > 0 ? Math.round((count / totalExceptions) * 100) : 0
      }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 5);

    // Enhanced date-wise exceptions with more details
    const dateMap: { [key: string]: any } = {};
    exceptions.forEach(exception => {
      const date = exception.mealDate;
      if (!dateMap[date]) {
        dateMap[date] = {
          date,
          count: 0,
          affectedStudents: 0,
          reasons: new Set(),
          mealTypes: new Set()
        };
      }
      dateMap[date].count++;
      dateMap[date].affectedStudents += exception.mealServedCount || 0;
      dateMap[date].reasons.add(exception.notServedReason || 'Other');
      dateMap[date].mealTypes.add(exception.mealType === 1 ? 'Lunch' : exception.mealType === 2 ? 'Breakfast' : 'Snack');
    });

    const dateWiseExceptions = Object.values(dateMap)
      .map((item: any) => ({
        ...item,
        reasons: Array.from(item.reasons),
        mealTypes: Array.from(item.mealTypes)
      }))
      .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime());

    // Mock weekly trend data
    const weeklyTrend = [
      { week: 'Week 1', count: 8 },
      { week: 'Week 2', count: 12 },
      { week: 'Week 3', count: 6 },
      { week: 'Week 4', count: totalExceptions }
    ];

    const summary = {
      totalExceptions,
      affectedClasses,
      affectedStudents,
      topReasons,
      dateWiseExceptions,
      weeklyTrend,
      exceptionRate: 0
    };

    setExceptionSummary(summary);
  }, []);

  // Initial data fetch
  useEffect(() => {
    (async () => {
      let userDetails = localStorage.getItem('users');
      let userData = JSON.parse(userDetails || '{}');
      let schoolId: any = userData?.[0]?.relatedEntity?.schools?.id;
      const classSectionIds =
        (userData?.[0]?.relatedEntity?.schoolClasses ?? [])
          .flatMap((c: any) => (c?.classSection ?? []).map((s: any) => s?.id))
          .filter((id: any): id is number => typeof id === 'number');

      const data: any = await fetchExceptionReportApi({
        schoolIds: [schoolId],
        classSectionIds,
        startDate: "2025-08-15",
        endDate: "2025-08-15",
      });
      const items = data?.data || [];
      setMealExceptions(items as any);
      calculateExceptionSummary(items as any);
    })();
  }, [calculateExceptionSummary]);

  // Filter exceptions based on search
  const filteredExceptions = Array.isArray(mealExceptions) ? mealExceptions.filter(exception => {
    const matchesSearch = searchTerm === '' ||
      exception.className?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exception.teacherName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exception.notServedReason?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      exception.mealServedRemarks?.toLowerCase().includes(searchTerm.toLowerCase());

    return matchesSearch;
  }) : [];

  // Export functions
  const handleExportExcel = async () => {
    if (!mealExceptions || mealExceptions.length === 0) {
      setError('No data to export');
      setShowErrorModal(true);
      return;
    }

    try {
      const exportColumns = [
        { title: 'Date', dataIndex: 'mealDate' },
        { title: 'Class', dataIndex: 'className' },
        { title: 'Section', dataIndex: 'sectionName' },
        { title: 'MealType', dataIndex: 'mealType' },
        { title: 'Meal Served', dataIndex: 'mealServedCount' },
        { title: 'Total Students', dataIndex: 'total' },
        { title: 'Action By', dataIndex: 'actionBy' },
        { title: 'Reason', dataIndex: 'notServedReason' },
      ];

      const fileName = `meal-report-${new Date().toISOString().split('T')[0]}.xlsx`;
      exportToExcel(mealExceptions, exportColumns, fileName);

      setSuccessMessage('Report exported to Excel successfully');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Export error:', error);
      setError('Failed to export report');
      setShowErrorModal(true);
    }
  };

  const handleExportPDF = async () => {
    if (!mealExceptions || mealExceptions.length === 0) {
      setError('No data to export');
      setShowErrorModal(true);
      return;
    }

    try {
      const exportColumns = [
        { title: 'Date', dataIndex: 'mealDate' },
        { title: 'Class', dataIndex: 'className' },
        { title: 'Section', dataIndex: 'sectionName' },
        { title: 'MealType', dataIndex: 'mealType' },
        { title: 'Meal Served', dataIndex: 'mealServedCount' },
        { title: 'Total Students', dataIndex: 'total' },
        { title: 'Action By', dataIndex: 'actionBy' },
        { title: 'Reason', dataIndex: 'notServedReason' },
      ];

      const fileName = `meal-report-${new Date().toISOString().split('T')[0]}.pdf`;
      exportToPDF(mealExceptions, exportColumns, fileName);

      setSuccessMessage('Report exported to PDF successfully');
      setShowSuccessModal(true);
    } catch (error) {
      console.error('Export error:', error);
      setError('Failed to export report');
      setShowErrorModal(true);
    }
  };

  const handleDateRangeChange = useCallback(async (startDate: string, endDate: string) => {
    let userDetails = localStorage.getItem('users');
    let userData = JSON.parse(userDetails || '{}');
    let schoolId: any = userData?.[0]?.relatedEntity?.schools?.id;
    const classSectionIds =
      (userData?.[0]?.relatedEntity?.schoolClasses ?? [])
        .flatMap((c: any) => (c?.classSection ?? []).map((s: any) => s?.id))
        .filter((id: any): id is number => typeof id === 'number');

    const data: any = await fetchExceptionReportApi({
      schoolIds: [schoolId],
      classSectionIds,
      startDate,
      endDate,
    });
    const items = data?.data || [];
    setMealExceptions(items as any);
    calculateExceptionSummary(items as any);
  }, [calculateExceptionSummary]);

   const getUserRole = useCallback(() => {
      try {
        const userData = getUserDetails();
        if (!userData || userData.length === 0) return null;
        return userData[0].role;
      } catch (error) {
        console.error('Error getting user role:', error);
        return null;
      }
    }, []);
  
    const userRole = getUserRole();
     const getDashboardRoute = (roleRaw: string) => {
        const role = roleRaw?.toLowerCase()?.trim();
    
        const direct: Record<string, keyof typeof all_routes> = {
          teacher: "teacherDashboard",
          mealsupervisor:"mealSupervisor"
    
        };
    
        if (role in direct) return all_routes[direct[role]];
    
        if (role?.includes("teacher")) return all_routes.teacherDashboard;
        if (role?.includes("meal supervisor") || role?.includes("Meal Supervisor")) return all_routes.mealSupervisor;
        const fallback: keyof typeof all_routes = "teacherDashboard";
        return all_routes[fallback] ?? "/";
      };
    
    
      const dashboardLink = useMemo(() => getDashboardRoute(userRole), [userRole]);

  return (
    <>
      <div>
        <div className="page-wrapper">
          <div className="content">
            {/* Page Header */}
            <div className="d-md-flex d-block align-items-center justify-content-between mb-3">
              <div className="my-auto mb-2">
                <h3 className="page-title mb-1">Meal Distribution Exception Report</h3>
                <nav>
                  <ol className="breadcrumb mb-0">
                    <li className="breadcrumb-item ">
                      <Link to={dashboardLink}>Dashboard</Link>  
                    </li>
                    <li className="breadcrumb-item">
                      <Link to="#">Report</Link>
                    </li>
                    <li className="breadcrumb-item active" aria-current="page">
                      Meal Exceptions
                    </li>
                  </ol>
                </nav>
              </div>

              <div className="d-flex my-xl-auto right-content align-items-center flex-wrap">
                {isLoading && (
                  <div className="me-3 d-flex align-items-center">
                    <div className="spinner-border spinner-border-sm me-2" style={{ color: '#4f6d9b' }} role="status">
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <small className="text-muted">Loading exceptions...</small>
                  </div>
                )}
              </div>
            </div>

            {/* Exception Summary Cards */}
            {exceptionSummary && !isLoading && (
              <div className="row mb-4">
                <div className="col-12">
                  <h5 className="mb-3" style={{ color: '#274472' }}>
                    <i className="ti ti-alert-triangle me-2" style={{ color: '#5885af' }}></i>
                    Exception Summary
                  </h5>
                </div>
                <div className="col-lg-3 col-md-6">
                  <div className="card text-dark">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          <h3 className="mb-1">{exceptionSummary.totalExceptions}</h3>
                          <p className="mb-0">Total Exceptions</p>
                        </div>
                        <i className="ti ti-alert-circle fs-1 opacity-50"></i>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6">
                  <div className="card text-dark" >
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          <h3 className="mb-1">{exceptionSummary.affectedClasses}</h3>
                          <p className="mb-0">Affected Classes</p>
                        </div>
                        <i className="ti ti-school fs-1 opacity-50"></i>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6">
                  <div className="card text-dark">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          <h3 className="mb-1">{exceptionSummary.affectedStudents}</h3>
                          <p className="mb-0">Affected Students</p>
                        </div>
                        <i className="ti ti-users fs-1 opacity-50"></i>
                      </div>
                    </div>
                  </div>
                </div>
                <div className="col-lg-3 col-md-6">
                  <div className="card text-dark">
                    <div className="card-body">
                      <div className="d-flex align-items-center">
                        <div className="flex-grow-1">
                          <h3 className="mb-1">{exceptionSummary?.exceptionRate || 0}%</h3>
                          <p className="mb-0">Exception Rate</p>
                        </div>
                        <i className="ti ti-percentage fs-1 opacity-50"></i>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Enhanced Charts Section */}
            {exceptionSummary && !isLoading && (
              <div className="row mb-4">
                <div className="col-lg-8">
                  <div className="card">
                    <div className="card-header">
                      <h5 className="card-title mb-0" >
                        <i className="ti ti-chart-line me-2"></i>
                        Date-wise Exception Trend
                      </h5>
                    </div>
                    <div className="card-body">
                      <div className="chart-container position-relative" style={{ height: '350px' }}>
                        {exceptionSummary.dateWiseExceptions.length > 0 ? (
                          <>
                            <div className="d-flex align-items-end justify-content-around h-100 pb-5">
                              {exceptionSummary.dateWiseExceptions.map((item, index) => {
                                const maxCount = Math.max(...exceptionSummary.dateWiseExceptions.map(d => d.count));
                                const height = maxCount > 0 ? (item.count / maxCount) * 250 : 0;
                                return (
                                  <div
                                    key={index}
                                    className="d-flex flex-column align-items-center position-relative"
                                    onMouseEnter={() => setHoveredChartData(item)}
                                    onMouseLeave={() => setHoveredChartData(null)}
                                    style={{ cursor: 'pointer' }}
                                  >
                                    <div
                                      className="rounded-top mb-2 position-relative transition-all"
                                      style={{
                                        width: '50px',
                                        height: `${height}px`,
                                        minHeight: item.count > 0 ? '25px' : '0px',
                                        backgroundColor: '#4F6D9B',
                                        opacity: hoveredChartData?.date === item.date ? '0.8' : '1',
                                        transform: hoveredChartData?.date === item.date ? 'scale(1.05)' : 'scale(1)',
                                        transition: 'all 0.2s ease'
                                      }}
                                    >
                                      <span className="position-absolute top-0 start-50 translate-middle-x text-white small fw-bold mt-1">
                                        {item.count}
                                      </span>
                                    </div>
                                    <small className="text-muted text-center" style={{ fontSize: '10px' }}>
                                      {new Date(item.date).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                                    </small>
                                  </div>
                                );
                              })}
                            </div>

                            {/* Enhanced Hover Tooltip */}
                            {hoveredChartData && (
                              <div
                                className="position-absolute text-white p-3 rounded shadow-lg"
                                style={{
                                  bottom: '70px',
                                  left: '50%',
                                  transform: 'translateX(-50%)',
                                  zIndex: 1000,
                                  minWidth: '280px',
                                  backgroundColor: '#274472'
                                }}
                              >
                                <div className="fw-bold mb-2 border-bottom pb-2">
                                  {new Date(hoveredChartData.date).toLocaleDateString('en-US', {
                                    weekday: 'long',
                                    year: 'numeric',
                                    month: 'long',
                                    day: 'numeric'
                                  })}
                                </div>

                                <div className="row g-2 small">
                                  <div className="col-6">
                                    <div className="fw-bold" style={{ color: '#c3e0e5' }}>
                                      <i className="ti ti-alert-circle me-1"></i>
                                      {hoveredChartData.count} Exceptions
                                    </div>
                                  </div>
                                  <div className="col-6">
                                    <div style={{ color: '#c3e0e5' }}>
                                      <i className="ti ti-users me-1"></i>
                                      {hoveredChartData.affectedStudents} Students
                                    </div>
                                  </div>
                                </div>

                                <div className="mt-2">
                                  <div className="small mb-1" style={{ color: '#5885af' }}>
                                    <i className="ti ti-list me-1"></i>
                                    <strong>Meal Types:</strong>
                                  </div>
                                  <div className="ms-3">
                                    {hoveredChartData.mealTypes.map((type: string, idx: number) => (
                                      <span key={idx} className="badge me-1 small" style={{ backgroundColor: '#4f6d9b', color: 'white' }}>
                                        {type}
                                      </span>
                                    ))}
                                  </div>
                                </div>

                                <div className="mt-2">
                                  <div className="small mb-1" style={{ color: '#5885af' }}>
                                    <i className="ti ti-alert-triangle me-1"></i>
                                    <strong>Top Reasons:</strong>
                                  </div>
                                  <div className="ms-3">
                                    {hoveredChartData.reasons.slice(0, 2).map((reason: string, idx: number) => (
                                      <div key={idx} className="small text-light mb-1">
                                        • {reason.length > 25 ? reason.substring(0, 25) + '...' : reason}
                                      </div>
                                    ))}
                                    {hoveredChartData.reasons.length > 2 && (
                                      <div className="small text-muted">
                                        +{hoveredChartData.reasons.length - 2} more reasons
                                      </div>
                                    )}
                                  </div>
                                </div>
                              </div>
                            )}
                          </>
                        ) : (
                          <div className="d-flex justify-content-center align-items-center h-100">
                            <div className="text-center">
                              <i className="ti ti-chart-bar-off fs-1 text-muted mb-2"></i>
                              <p className="text-muted">No exception data available</p>
                            </div>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                <div className="col-lg-4">
                  <div className="card">
                    <div className="card-header">
                      <h5 className="card-title mb-0">
                        <i className="ti ti-list-details me-2"></i>
                        Top Exception Reasons
                      </h5>
                    </div>
                    <div className="card-body">
                      {exceptionSummary.topReasons.length > 0 ? (
                        <div className="space-y-3">
                          {exceptionSummary.topReasons.map((reason, index) => (
                            <div key={index} className="mb-3">
                              <div className="d-flex justify-content-between align-items-center mb-1">
                                <span className="fw-medium text-truncate" style={{ maxWidth: '60%' }}>
                                  {reason.reason}
                                </span>
                                <div className="d-flex align-items-center">
                                  <span className="badge me-2" style={{ backgroundColor: '#274472', color: 'white' }}>{reason.count}</span>
                                  <small className="text-muted">{reason.percentage}%</small>
                                </div>
                              </div>
                              <div className="progress" style={{ height: '6px' }}>
                                <div
                                  className="progress-bar"
                                  style={{ width: `${reason.percentage}%`, backgroundColor: '#4f6d9b' }}
                                ></div>
                              </div>
                            </div>
                          ))}
                        </div>
                      ) : (
                        <div className="text-center py-3">
                          <i className="ti ti-list-x fs-1 text-muted mb-2"></i>
                          <p className="text-muted mb-0">No exception reasons found</p>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Exceptions Data Table */}
            {!isLoading && (
              <div className="card">
                <div className="card-header">
                  <div className="d-flex justify-content-between align-items-center">
                    <h5 className="card-title mb-0">
                      <i className="ti ti-table me-2"></i>
                      Detailed Exception Records
                    </h5>
                    <div className="d-flex align-items-center">
                      {/* Export buttons */}
                      {filteredExceptions.length > 0 && (
                        <TooltipOption
                          onExportPDF={handleExportPDF}
                          onExportExcel={handleExportExcel}
                        />
                      )}


                      <div className="input-icon-start mb-3 me-2 position-relative">
                        <PredefinedDateRanges onDateChange={handleDateRangeChange} />
                      </div>
                    </div>
                  </div>
                </div>
                <div className="card-body">
                  {filteredExceptions.length > 0 ? (
                    <div className="table-responsive">
                      <table className="table table-striped table-hover">
                        <thead>
                          <tr>
                            <th>Date</th>
                            <th>Class/Section</th>
                            <th>Meal Type</th>
                            <th>Affected Students</th>
                            <th>Teacher</th>
                            <th>Reason</th>
                          </tr>
                        </thead>
                        <tbody>
                          {filteredExceptions.map((exception, index) => (
                            <tr key={exception.id || index}>
                              <td>
                                <span className="fw-medium">
                                  {new Date(exception.mealDate).toLocaleDateString()}
                                </span>
                              </td>
                              <td>
                                <div>
                                  <span className=" mb-1" style={{ color: 'black' }}>
                                    {exception.className}
                                  </span>
                                  <br />
                                  <small className="text-muted">Section {exception.sectionName}</small>
                                </div>
                              </td>
                              <td>
                                <span className=" text-dark">
                                  {exception.mealType}
                                </span>
                              </td>
                              <td>
                                <span className="fw-bold" style={{ color: '#274472' }}>
                                  {exception.mealServedCount}
                                </span>
                                <small className="text-muted d-block">of {exception.total}</small>
                              </td>
                              <td>
                                <span className="text-dark">
                                  {exception?.actionBy}
                                </span>
                              </td>
                              <td>
                                <span className="fw-medium text-dark">
                                  {exception.notServedReason || 'No Reason'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  ) : (
                    <div className="text-center py-5">
                      {searchTerm ? (
                        <>
                          <i className="ti ti-search-off fs-1 text-muted mb-3"></i>
                          <h5 className="text-muted">No Records Found</h5>
                          <p className="text-muted">
                            Try adjusting your search criteria.
                          </p>
                          <button
                            className="btn"
                            style={{ backgroundColor: '#4f6d9b', color: 'white', borderColor: '#4f6d9b' }}
                            onClick={() => setSearchTerm('')}
                          >
                            Clear Search
                          </button>
                        </>
                      ) : (
                        <>
                          <i className="ti ti-check-circle fs-1 mb-3" style={{ color: '#5885af' }}></i>
                          <h5 style={{ color: '#5885af' }}>No Meal Exceptions Found!</h5>
                          <p className="text-muted">
                            All meals were served successfully during the selected period.
                          </p>
                        </>
                      )}
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Loading State */}
            {isLoading && (
              <div className="d-flex justify-content-center align-items-center py-5">
                <div className="spinner-border" style={{ color: '#4f6d9b' }} role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <span className="ms-3">Loading meal exception data...</span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Error Modal */}
      <ErrorModal
        show={showErrorModal}
        onClose={() => setShowErrorModal(false)}
        message={error}
      />

      {/* Success Modal */}
      <SuccessModal
        show={showSuccessModal}
        onClose={() => setShowSuccessModal(false)}
        message={successMessage}
      />
    </>
  );
};

export default MealExceptionReport;